import { createWalletsRefill, WalletsRefill } from "../refillApi"
import { StandardMainPanel } from "../../../../../../../shared/components/forms/StandardMainPanel"
import { LocalizedEnum, LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { Grid, MenuItem, Stack, Typography } from "@mui/material"
import { useMoney } from "../../../../../../currency/useMoney"
import { toDinero } from "../../../../../../currency/currencyLib"
import * as yup from "yup"
import { Form, Formik } from "formik"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { FormikDropdown, FormikTextField } from "../../../../../../../shared/components/forms/Formik"
import { StandardLoadingButton } from "../../../../../../../shared/components/buttons/LoadingButton"
import React, { useCallback } from "react"
import { isPaymentGatewayDetails, PaymentMethodType } from "../../../../../../payments/paymentTypes"
import { StandardPaymentMethodIcon } from "../../../../../../../shared/components/payment/StandardPaymentMethodIcon"
import { SelectableRadioCard } from "../../../../../../../shared/components/selectableRadioCard/SelectableRadioCard"
import { makeStyles } from "tss-react/mui"
import { useApiMutation } from "../../../../../../../core/api/useApiMutation"
import { RefillAmount, RefillFormValues, UserWallet, WalletsOrderTypeRefill } from "../refillTypes"
import { amountToNumber } from "../helpers"
import { UserCardView } from "./SelectWalletDialog"
import { isEmpty } from "lodash"
import { useAppSelector } from "../../../../../../store/storeHooks"
import { selectEffectiveUserId } from "../../../../../../context/contextSelectors"
import { isRefillWalletResponse, RefillWalletResponse } from "../../../../../../payments/transactionTypes"
import { useNavigate } from "react-router-dom"

const useStyles = makeStyles()(() => ({
    input: {
        width: "20rem",
    },
    button: {
        width: "20rem",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
    },
    paymentMethodContainer: {
        width: "20rem",
    },
    paymentMethodList: {
        display: "flex",
        flexDirection: "column",
        gap: ".75rem",
    },
}))

export const MAX_WALLET_BALANCE = 3000
export const MAX_INPUT_REFILL_LENGTH = 4
export const INVALID_INPUT_KEY = [".", ",", "e", "E", "-", "+"]

type Props = Readonly<{
    walletDetails: UserWallet
}>

export function WalletsRefillManual({ walletDetails }: Props) {
    const { classes } = useStyles()
    const moneyFactory = useMoney({})
    const navigate = useNavigate()
    const { l10n } = useAppLocalization()
    const userId = useAppSelector(selectEffectiveUserId) || 0
    const isAutomatic = !isEmpty(walletDetails.paymentDetails.refill.automatic?.subscription)

    const defaultInitialValues: RefillFormValues = {
        paymentMethod: "",
        amount: "",
        customAmount: "",
    }

    const { mutate, isLoading: isSubmitting } = useApiMutation(createWalletsRefill, {
        async onSuccess(_, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [WalletsRefill.WALLET_REFILL_DETAILS] })
        },
    })
    const validationSchema = yup.object({
        paymentMethod: yup.string().required(l10n.getString("required-field")),
        amount: yup.string().required(l10n.getString("required-field")),
        customAmount: yup
            .number()
            .notOneOf([0], l10n.getString("user-wallets-refill-manual-main-amount-zero-error"))
            .test("max-total-balance", "", function (value) {
                if (this.parent.amount !== RefillAmount.AMOUNT_OTHER || !value) {
                    return true
                }

                const currentBalanceWithScale = walletDetails.balance?.amount || 0
                const balanceScale = walletDetails.balance?.scale || 0
                const currentBalance =
                    balanceScale > 0 ? currentBalanceWithScale / Math.pow(10, balanceScale) : currentBalanceWithScale

                const maxRefillAmount = Math.floor(MAX_WALLET_BALANCE - currentBalance)

                if (currentBalance + Number(value) > MAX_WALLET_BALANCE) {
                    return this.createError({
                        message: l10n.getString("user-wallets-refill-manual-main-amount-error", {
                            maxBalance: moneyFactory.format(
                                toDinero({
                                    amount: MAX_WALLET_BALANCE,
                                    currency: "DKK",
                                    scale: 0,
                                }),
                            ),
                            maxRefillAmount: moneyFactory.format(
                                toDinero({
                                    amount: maxRefillAmount,
                                    currency: "DKK",
                                    scale: 0,
                                }),
                            ),
                        }),
                    })
                }

                return true
            })
            .when("amount", {
                is: (type: RefillAmount) => type === RefillAmount.AMOUNT_OTHER,
                then: (schema) => schema.required(l10n.getString("required-field")),
                otherwise: (schema) => schema.notRequired(),
            }),
    })

    const transactionResponseHandler = useCallback((response: RefillWalletResponse) => {
        if (isRefillWalletResponse(response)) {
            if (isPaymentGatewayDetails(response.paymentDetails)) {
                const { paymentWindowUrl } = response.paymentDetails

                window.location.href = paymentWindowUrl
            }
        }
    }, [])

    function handleSubmit(values: RefillFormValues) {
        const refillAmount = +values.customAmount || amountToNumber(values.amount)

        mutate(
            {
                userId,
                acquirerSetupId: walletDetails.acquirer.acquirerSetupId,
                walletId: walletDetails.id ? walletDetails.id : 0,
                order: {
                    orderType: WalletsOrderTypeRefill.REFILL,
                    payment: {
                        method: values.paymentMethod as PaymentMethodType,
                    },
                    amount: {
                        currency: "DKK",
                        amount: refillAmount,
                        scale: 0,
                    },
                },
            },
            {
                onSuccess: transactionResponseHandler,
            },
        )
    }

    return (
        <Formik
            enableReinitialize
            initialValues={defaultInitialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
        >
            {({ values, setFieldValue, errors, submitCount, isValid }) => (
                <Form>
                    <Grid container spacing="1.5rem" columns={{ xs: 8, md: 12 }}>
                        <Grid item xs={8} md={8}>
                            <StandardMainPanel>
                                <Stack direction="column" spacing="2rem">
                                    <LocalizedStrict
                                        id="user-wallets-refill-manual-main-amount-label"
                                        attrs={{ label: true }}
                                    >
                                        <FormikDropdown
                                            onChange={() => setFieldValue("customAmount", "")}
                                            className={classes.input}
                                            name="amount"
                                            label="Amount"
                                        >
                                            {Object.keys(RefillAmount).map((value) => (
                                                <MenuItem value={value} key={value}>
                                                    <LocalizedEnum
                                                        base="user-wallets-refill-manual-main-amount"
                                                        enumValue={value}
                                                    >
                                                        <span>{value}</span>
                                                    </LocalizedEnum>
                                                </MenuItem>
                                            ))}
                                        </FormikDropdown>
                                    </LocalizedStrict>
                                    {values.amount === RefillAmount.AMOUNT_OTHER && (
                                        <LocalizedStrict
                                            id="user-wallets-refill-manual-main-custom-amount-label"
                                            attrs={{ label: true }}
                                        >
                                            <FormikTextField
                                                className={classes.input}
                                                fullWidth
                                                type="number"
                                                name="customAmount"
                                                label="Enter amount:"
                                                inputProps={{
                                                    inputMode: "numeric",
                                                    maxLength: MAX_INPUT_REFILL_LENGTH,
                                                    onInput: (e: React.FormEvent<HTMLInputElement>) => {
                                                        const currentValue = e.currentTarget.value

                                                        const cleanValue = currentValue.replace(/[^0-9]/g, "")

                                                        const limitedValue = cleanValue.slice(
                                                            0,
                                                            MAX_INPUT_REFILL_LENGTH,
                                                        )

                                                        if (currentValue !== limitedValue) {
                                                            e.currentTarget.value = limitedValue
                                                            setFieldValue("customAmount", limitedValue)
                                                        }
                                                    },
                                                    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                                                        if (INVALID_INPUT_KEY.includes(e.key)) {
                                                            e.preventDefault()
                                                        }
                                                    },
                                                }}
                                            />
                                        </LocalizedStrict>
                                    )}

                                    <Stack className={classes.paymentMethodContainer} direction="column">
                                        <LocalizedStrict id="user-wallets-refill-manual-main-payment-method">
                                            <Typography variant="subtitle1" mb="1rem">
                                                Payment methods:
                                            </Typography>
                                        </LocalizedStrict>
                                        <Stack className={classes.paymentMethodList}>
                                            {walletDetails.paymentDetails.refill?.manual?.paymentMethods?.map(
                                                (paymentMethod) => (
                                                    <SelectableRadioCard
                                                        key={paymentMethod.value}
                                                        isSelected={values.paymentMethod === paymentMethod.value}
                                                        onSelect={() =>
                                                            setFieldValue("paymentMethod", paymentMethod.value)
                                                        }
                                                        endAdornment={
                                                            <StandardPaymentMethodIcon icon={paymentMethod.icon} />
                                                        }
                                                    >
                                                        <LocalizedEnum
                                                            base="purchases-payment-method"
                                                            enumValue={paymentMethod.value}
                                                        >
                                                            <span>{paymentMethod.value}</span>
                                                        </LocalizedEnum>
                                                    </SelectableRadioCard>
                                                ),
                                            )}
                                        </Stack>
                                        {submitCount > 0 && errors?.paymentMethod && (
                                            <LocalizedStrict id="user-wallets-refill-payment-method-error">
                                                <Typography p=".5rem 1rem" color="error" fontSize="0.75rem">
                                                    Payment method is required
                                                </Typography>
                                            </LocalizedStrict>
                                        )}
                                    </Stack>
                                    <StandardLoadingButton
                                        className={classes.button}
                                        type="submit"
                                        fullWidth={false}
                                        loading={isSubmitting}
                                        disabled={submitCount > 0 && !isValid}
                                    >
                                        <LocalizedStrict id="user-wallets-refill-manual-main-perform-refill-button">
                                            <Typography textAlign="start" fontWeight={500}>
                                                Confirm payment
                                            </Typography>
                                        </LocalizedStrict>
                                        <Typography textAlign="end" fontWeight={500}>
                                            {moneyFactory.format(
                                                toDinero({
                                                    amount: values.customAmount
                                                        ? +values.customAmount
                                                        : amountToNumber(values.amount),
                                                    currency: "DKK",
                                                    scale: 0,
                                                }),
                                            )}
                                        </Typography>
                                    </StandardLoadingButton>
                                </Stack>
                            </StandardMainPanel>
                        </Grid>

                        {walletDetails && (
                            <Grid item xs={8} md={4}>
                                <LocalizedStrict
                                    id={
                                        isAutomatic
                                            ? "user-wallets-refill-automatic-type"
                                            : "user-wallets-refill-manual-type"
                                    }
                                    attrs={{
                                        statusLabel: true,
                                    }}
                                >
                                    <UserCardView
                                        balance={walletDetails.balance}
                                        displayName={walletDetails.displayName}
                                        brandingLabel={walletDetails.brandingLabel}
                                        statusLabel={isAutomatic ? "Automatic" : "Manual"}
                                        onSelect={() => {}}
                                    />
                                </LocalizedStrict>
                            </Grid>
                        )}
                    </Grid>
                </Form>
            )}
        </Formik>
    )
}
